import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BackArrow, InfoBlock, TopProgress } from '../../components'
import { dedicatedServersService } from '../../api'
import { useUi } from '../../context/ui.context'
import { usePageTitle } from '../../context/navigationContext'
import { extractErrorMessage } from '../../utils/error-extractor'
import { formatDate } from '../../utils/date-formatter'
import { DedicatedIpAssignmentDetails } from './types'
import { DetailContainer, DetailGrid, DetailHeader, DetailTitle, RawData } from './styles'

const DedicatedIpAssignmentDetailsPage: FC = () => {
  const [t] = useTranslation()
  const { assignmentId } = useParams()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  const [assignment, setAssignment] = useState<DedicatedIpAssignmentDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const title = assignment?.ip || String(t('DEDICATED_IP_ASSIGNMENT'))
  usePageTitle(title)

  useEffect(() => {
    if (!assignmentId) return
    setLoading(true)
    dedicatedServersService.getIpAssignment(assignmentId)
      .then((response) => setAssignment(response.data))
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('FAILED_TO_LOAD_DEDICATED_IP'),
        })
        navigate('/dedicated-servers/ips')
      })
      .finally(() => setLoading(false))
  }, [assignmentId, navigate, t, viewAlert])

  const info = useMemo(() => assignment ? [
    { title: t('ID'), value: assignment.id, copy: true },
    { title: t('EXTERNAL_ID'), value: assignment.external_id ?? '-' },
    { title: t('DEDICATED_IP_ADDRESS'), value: assignment.ip, copy: true },
    { title: t('DEDICATED_PRIMARY'), value: assignment.is_primary ? t('YES') : t('NO') },
    { title: t('DEDICATED_SUBNET'), value: assignment.subnet || '-' },
    { title: 'CIDR', value: assignment.cidr ?? '-' },
    { title: t('DEDICATED_GATEWAY'), value: assignment.gateway || '-' },
    { title: t('DEDICATED_SYNCED_AT'), value: formatDate(assignment.synced_at) },
    { title: t('UPDATED_AT'), value: formatDate(assignment.updated_at_external) },
  ] : [], [assignment, t])

  const serverInfo = useMemo(() => assignment ? [
    {
      title: t('ID'),
      value: assignment.server?.id || '-',
      href: assignment.server?.id ? `/dedicated-servers/servers/${assignment.server.id}` : undefined,
    },
    { title: t('EXTERNAL_ID'), value: assignment.server?.external_id ?? '-' },
    { title: t('NAME'), value: assignment.server?.name || '-' },
    { title: t('DEDICATED_HOSTNAME'), value: assignment.server?.hostname || '-' },
    { title: t('DEDICATED_SERVER_NAME'), value: assignment.server?.servername || '-' },
    { title: t('DEDICATED_EXTERNAL_USER_ID'), value: assignment.server?.external_user_id ?? '-' },
    {
      title: t('DEDICATED_INTERNAL_USER_ID'),
      value: assignment.server?.dedicated_user_id || '-',
      href: assignment.server?.dedicated_user_id ? `/dedicated-servers/users/${assignment.server.dedicated_user_id}` : undefined,
    },
  ] : [], [assignment, t])

  if (loading) return <TopProgress loading />
  if (!assignment) return null

  return (
    <DetailContainer>
      <BackArrow fallbackPath="/dedicated-servers/ips" />
      <DetailHeader>
        <DetailTitle>{title}</DetailTitle>
      </DetailHeader>
      <DetailGrid>
        <InfoBlock data={info} title={t('GENERAL_INFORMATION')} />
        <InfoBlock data={serverInfo} title={t('DEDICATED_SERVER')} />
      </DetailGrid>
      {assignment.raw_data !== undefined && assignment.raw_data !== null && (
        <div style={{ marginTop: 12, marginRight: 10 }}>
          <InfoBlock
            data={[]}
            title={t('DEDICATED_RAW_DATA')}
            customAddComponent={<RawData>{JSON.stringify(assignment.raw_data, null, 2)}</RawData>}
          />
        </div>
      )}
    </DetailContainer>
  )
}

export default DedicatedIpAssignmentDetailsPage
